import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, first, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { DataService } from './data.service';
import { ApiPaginatedResponse } from './pagination.service';
import {
  Survey,
  SurveyFormData,
  SurveySubmission,
  SurveySubmissionFilters,
  SurveyStatistics,
  WhatsappRuleFormData
} from '../interface/survey-v2';

@Injectable({
  providedIn: 'root'
})
export class SurveyV2Service {
  private _isFromMatrix = true;
  private baseUrl = `${environment.mkgoURL}/api/v1/survey`;
  private submissionUrl = `${environment.mkgoURL}/api/v1/survey-submission`;

  constructor(
    private _http: HttpClient,
    private _dataService: DataService
  ) { }

  async getAllSurveys(): Promise<Survey[]> {
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response: { surveys: Survey[] } = await this._http.get(this.baseUrl, options).pipe(first()).toPromise() as any;
    return response.surveys;
  }

  async getSurveysPaginated(page: number = 1, limit: number = 10): Promise<ApiPaginatedResponse<Survey>> {
    const url = `${this.baseUrl}/pages?page=${page}&limit=${limit}&sort=updatedAt&order=-1`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.get<ApiPaginatedResponse<Survey>>(url, options).pipe(first()).toPromise();
    return response;
  }

  async createSurvey(survey: SurveyFormData): Promise<Survey> {
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.post(this.baseUrl, survey, options).pipe(first()).toPromise();
    return response as Survey;
  }

  async getSurvey(id: string): Promise<Survey> {
    const url = `${this.baseUrl}/${id}`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.get(url, options).pipe(first()).toPromise();
    return response as Survey;
  }

  async updateSurvey(id: string, survey: SurveyFormData): Promise<Survey> {
    const url = `${this.baseUrl}/${id}`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.put(url, survey, options).pipe(first()).toPromise();
    return response as Survey;
  }

  async deleteSurvey(id: string): Promise<void> {
    const url = `${this.baseUrl}/${id}`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    await this._http.delete(url, options).pipe(first()).toPromise();
  }

  async activateSurvey(id: string): Promise<Survey> {
    const url = `${this.baseUrl}/${id}/activate`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.put(url, {}, options).pipe(first()).toPromise();
    return response as Survey;
  }

  async deactivateSurvey(id: string): Promise<Survey> {
    const url = `${this.baseUrl}/${id}/deactivate`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.put(url, {}, options).pipe(first()).toPromise();
    return response as Survey;
  }

  async getActiveSurveys(): Promise<Survey[]> {
    const url = `${this.baseUrl}/actives`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response: { surveys: Survey[] } = await this._http.get(url, options).pipe(first()).toPromise() as any;
    return response.surveys;
  }

  async getInactiveSurveys(): Promise<Survey[]> {
    const url = `${this.baseUrl}/inactives`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response: { surveys: Survey[] } = await this._http.get(url, options).pipe(first()).toPromise() as any;
    return response.surveys;
  }

  // Survey Submissions (Envios e Respostas)
  async getAllSubmissions(filters?: SurveySubmissionFilters): Promise<SurveySubmission[]> {
    let url = this.submissionUrl;
    if (filters) {
      const params = new URLSearchParams();
      if (filters.survey) params.append('survey', filters.survey);
      if (filters.status) params.append('status', filters.status.toString());
      if (filters.startDate) params.append('start', filters.startDate);
      if (filters.endDate) params.append('end', filters.endDate);
      if (filters.client) params.append('client', filters.client);
      if (filters.os) params.append('os', filters.os);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }
    }

    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response: { submissions: SurveySubmission[] } = await this._http.get(url, options).pipe(first()).toPromise() as any;
    return response.submissions;
  }

  async getSubmissionsPaginated(page: number = 1, limit: number = 10, filters?: SurveySubmissionFilters): Promise<ApiPaginatedResponse<SurveySubmission>> {
    let url = `${this.submissionUrl}/pages?page=${page}&limit=${limit}&sort=updatedAt&order=-1`;

    if (filters) {
      const params = new URLSearchParams();
      if (filters.survey) params.append('survey', filters.survey);
      if (filters.status) params.append('status', filters.status.toString());
      if (filters.startDate) params.append('start', filters.startDate);
      if (filters.endDate) params.append('end', filters.endDate);
      if (filters.client) params.append('client', filters.client);
      if (filters.os) params.append('os', filters.os);

      if (params.toString()) {
        url += `&${params.toString()}`;
      }
    }

    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.get<ApiPaginatedResponse<SurveySubmission>>(url, options).pipe(first()).toPromise();
    return response;
  }

  async createSubmission(submission: { survey: string; os: string; client: string }): Promise<SurveySubmission> {
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.post(this.submissionUrl, submission, options).pipe(first()).toPromise();
    return response as SurveySubmission;
  }

  async getSubmission(id: string): Promise<SurveySubmission> {
    const url = `${this.submissionUrl}/${id}`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.get(url, options).pipe(first()).toPromise();
    return response as SurveySubmission;
  }

  async deleteSubmission(id: string): Promise<void> {
    const url = `${this.submissionUrl}/${id}`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    await this._http.delete(url, options).pipe(first()).toPromise();
  }

  async submitAnswers(submissionId: string, answers: { questionId: string; value: any }[]): Promise<SurveySubmission> {
    const url = `${this.submissionUrl}/${submissionId}/answers`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.put(url, { answers }, options).pipe(first()).toPromise();
    return response as SurveySubmission;
  }

  async getSubmissionByOsId(osId: string): Promise<SurveySubmission[]> {
    const url = `${this.submissionUrl}/os/${osId}`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response: { submissions: SurveySubmission[] } = await this._http.get(url, options).pipe(first()).toPromise() as any;
    return response.submissions;
  }

  async getStatistics(surveyId: string): Promise<SurveyStatistics> {
    const url = `${this.submissionUrl}/statistics/${surveyId}`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.get(url, options).pipe(first()).toPromise();
    return response as SurveyStatistics;
  }

  async getOsWithoutSurvey(): Promise<any> {
    const url = `${this.submissionUrl}/os-without-survey`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.get<any>(url, options).pipe(first()).toPromise();
    return response || { os: [] };
  }

  // WhatsApp Rules methods
  async getWhatsappRules(surveyId: string): Promise<{ whatsappRules: WhatsappRuleFormData[] }> {
    const url = `${this.baseUrl}/${surveyId}/whatsapp-rules`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.get(url, options).pipe(first()).toPromise();
    return response as { whatsappRules: WhatsappRuleFormData[] };
  }

  async addWhatsappRule(surveyId: string, rule: WhatsappRuleFormData): Promise<Survey> {
    const url = `${this.baseUrl}/${surveyId}/whatsapp-rules`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.post(url, rule, options).pipe(first()).toPromise();
    return response as Survey;
  }

  async updateWhatsappRule(surveyId: string, ruleId: string, rule: Partial<WhatsappRuleFormData>): Promise<Survey> {
    const url = `${this.baseUrl}/${surveyId}/whatsapp-rules/${ruleId}`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.put(url, rule, options).pipe(first()).toPromise();
    return response as Survey;
  }

  async removeWhatsappRule(surveyId: string, ruleId: string): Promise<Survey> {
    const url = `${this.baseUrl}/${surveyId}/whatsapp-rules/${ruleId}`;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    const response = await this._http.delete(url, options).pipe(first()).toPromise();
    return response as Survey;
  }
}
